import { AxiosHeaders, AxiosRequestConfig, AxiosRequestHeaders, CanceledError } from 'axios';
import { getResolvedApiBaseUrl } from '../utils/environmentRouting';

export const OPERATIONS_LOGOUT_SIGNAL = 'modal.operations.logout.v1';
export const OPERATIONS_LOGIN_SIGNAL = 'modal.operations.login.v1';
export const OPERATIONS_LOCAL_LOCK = 'modal.operations.lock.v1';
export const OPERATIONS_SESSION_INVALID = 'modal-operations-session-invalid';
export function getOperationsBranding(hostname = typeof window === 'undefined' ? '' : window.location.hostname) {
  const host = hostname.toLowerCase();
  const dev = host === 'localhost' || host === '127.0.0.1' || host === 'dev.modalhub.net' || host.endsWith('.dev.modalhub.net');
  const assets = dev ? '/operations' : '/operations/production';
  return {
    label: dev ? 'DEV' : 'PROD',
    title: dev ? 'MODAL Operations DEV' : 'MODAL Operations',
    centralUrl: dev ? 'https://globaladmin.dev.modalhub.net/operations/' : 'https://globaladmin.modalhub.net/operations/',
    manifest: `${assets}/manifest.webmanifest`,
    icon: `${assets}/icon.svg`,
    touchIcon: `${assets}/icon-192.png`,
  };
}

type Scope = { key: string; companyId: string; csrfToken: string; controller: AbortController };
let scope: Scope | null = null;
let generation = 0;

export function isOperationsPath(path = typeof window === 'undefined' ? '' : window.location.pathname): boolean {
  // React Router decodes individual segments and matches routes case-insensitively.
  // Decode only the entry segment; never turn encoded slashes into route separators.
  const entry = path.split('/')[1] || '';
  try { return decodeURIComponent(entry).toLowerCase() === 'operations'; }
  catch { return entry.toLowerCase() === 'operations'; }
}

export function canonicalOperationsPath(path: string): string {
  if (!isOperationsPath(path)) return '/operations/assistant';
  try {
    const segments = path.split('/').map((segment) => decodeURIComponent(segment));
    // Operations has static routes only. Reject reserved characters rather than reinterpret them.
    if (segments.some((segment) => !/^[a-z0-9-]*$/i.test(segment))) return '/operations/assistant';
    return segments.join('/').toLowerCase();
  } catch { return '/operations/assistant'; }
}

export function isOperationsHost(hostname = typeof window === 'undefined' ? '' : window.location.hostname): boolean {
  return ['globaladmin.dev.modalhub.net', 'globaladmin.modalhub.net', 'localhost', '127.0.0.1'].includes(hostname.toLowerCase());
}

export function getOperationsApiBaseUrl(hostname = typeof window === 'undefined' ? '' : window.location.hostname): string {
  // Hosted cookie requests must be cross-origin so the browser supplies Origin on GET too.
  // The local proxy is only for local development/fixtures, not a hosted session fallback.
  const host = hostname.toLowerCase();
  if (host === 'globaladmin.dev.modalhub.net') {
    if (process.env.REACT_APP_API_URL === 'https://api.modalhub.net') throw new Error('This Operations build cannot access DEV.');
    return 'https://api.dev.modalhub.net';
  }
  if (host === 'globaladmin.modalhub.net') {
    if (process.env.REACT_APP_API_URL === 'https://api.dev.modalhub.net') throw new Error('This Operations build cannot access production.');
    return 'https://api.modalhub.net';
  }
  return getResolvedApiBaseUrl();
}

export function clearOperationsScope(): void {
  scope?.controller.abort();
  scope = null;
  generation += 1;
}

export function setOperationsScope(actorId: string, companyId: string, csrfToken: string): string {
  clearOperationsScope();
  const key = `${actorId}:${companyId}:${generation}`;
  scope = { key, companyId, csrfToken, controller: new AbortController() };
  return key;
}

export function assertOperationsScope(key: string): void {
  if (scope?.key !== key || !isOperationsHost() || navigator.onLine === false) {
    throw new CanceledError('Operations context changed. Check the original receipt before continuing.');
  }
}

type OperationsRequest = AxiosRequestConfig & { modalOperationsScope?: string; modalOperationsCleanup?: () => void };

export function applyOperationsScope<T extends OperationsRequest>(config: T): T & OperationsRequest {
  if (!isOperationsPath()) return config;
  if (!scope) throw new CanceledError('An authoritative Operations session and company selection are required.');
  assertOperationsScope(scope.key);
  if (!config.url?.startsWith('/') || config.url.startsWith('//') || /[\\\r\n]/.test(config.url)) {
    throw new CanceledError('Operations requests must use the central API.');
  }
  config.baseURL = getOperationsApiBaseUrl();
  config.withCredentials = true;
  config.modalOperationsScope = scope.key;
  // Axios flattens method/default headers before this request interceptor runs.
  const headers = AxiosHeaders.from(config.headers as AxiosRequestHeaders | undefined);
  config.headers = headers;
  const requestedCompany = headers.get('X-Modal-Company-Id');
  if (requestedCompany && requestedCompany !== scope.companyId) {
    throw new CanceledError('The requested company no longer matches this Operations context.');
  }
  delete config.auth;
  headers.delete('Authorization');
  headers.delete('X-Modal-CSRF');
  headers['X-Original-Host'] = window.location.host.toLowerCase();
  headers['X-Modal-Company-Id'] = scope.companyId;
  headers['Cache-Control'] = 'no-store';
  if (!['get', 'head', 'options'].includes((config.method || 'get').toLowerCase())) {
    headers['X-Modal-CSRF'] = scope.csrfToken;
  }
  const controller = new AbortController();
  const cancel = () => controller.abort();
  const signals = [scope.controller.signal, config.signal];
  signals.forEach((signal) => {
    if (signal?.aborted) cancel();
    else signal?.addEventListener?.('abort', cancel, { once: true });
  });
  config.modalOperationsCleanup = () => signals.forEach((signal) => signal?.removeEventListener?.('abort', cancel));
  config.signal = controller.signal;
  return config;
}

export function checkOperationsResponse(config?: OperationsRequest): void {
  config?.modalOperationsCleanup?.();
  if (config?.modalOperationsScope) assertOperationsScope(config.modalOperationsScope);
}

export function operationsSelectionKey(actorId: string, grantVersion: number | string): string {
  return `modal.operations.company.v1:${actorId}:${grantVersion}`;
}

export function readOperationsLock(): boolean {
  try { if (localStorage.getItem(OPERATIONS_LOCAL_LOCK)) return true; } catch { /* Try tab storage. */ }
  try { return sessionStorage.getItem(OPERATIONS_LOCAL_LOCK) === 'locked'; } catch { return false; }
}

export function saveOperationsLock(locked: boolean): void {
  // This marker contains no authority or session data. It prevents a failed logout
  // from silently restoring a still-valid HttpOnly cookie after a reload.
  for (const storage of ['localStorage', 'sessionStorage'] as const) {
    try {
      if (locked) window[storage].setItem(OPERATIONS_LOCAL_LOCK, 'locked');
      else window[storage].removeItem(OPERATIONS_LOCAL_LOCK);
    } catch { /* The current workspace still locks when storage is unavailable. */ }
  }
}

export function readOperationsSelection(actorId: string, grantVersion: number | string): string | null {
  try { return sessionStorage.getItem(operationsSelectionKey(actorId, grantVersion)); }
  catch { return null; }
}

export function saveOperationsSelection(actorId: string, grantVersion: number | string, companyId: string): void {
  try { sessionStorage.setItem(operationsSelectionKey(actorId, grantVersion), companyId); }
  catch { /* Selection remains usable in memory when storage is unavailable. */ }
}

export function bindOperationsAPI<T extends object>(api: T, key?: string): T {
  if (!key) return api;
  return new Proxy(api, {
    get(target, property) {
      const value = Reflect.get(target, property);
      if (typeof value !== 'function') return value;
      return (...args: unknown[]) => {
        // This check also covers follow-up calls after an awaited, now obsolete action.
        assertOperationsScope(key);
        return value.apply(target, args);
      };
    },
  });
}
